import { useId } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import type { StatementLineView, StatementView } from '@/api/generated';
import { MoneyCell, NumericCell, TotalsFooter } from '@/components/cells';
import { moneyMicroIn } from '@/components/moneyIn';
import { useCivilRange } from '@/i18n/useCivilRange';
import { enumLabel } from '@/i18n/enumLabel';
import { useLocale } from '@/i18n/useLocale';
import { resolveTokens } from '@/theme';
import { downloadCsv } from '@/utils/csv';
import { statementCsv, statementFilename } from './statementCsv';

export interface StatementLinesProps {
  statement: StatementView;
  slug: string;
}

/** One subscriber statement's lines, with the CSV export (the tenant's ledger for it). */
export function StatementLines({ statement, slug }: StatementLinesProps) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const headingId = useId();
  const dash = t('statements.unavailable');
  const currency = statement.currency;
  const lines: StatementLineView[] = statement.lines;
  const civilRange = useCivilRange(dash);
  const period = civilRange(statement.periodStart, statement.periodEnd);

  return (
    <Paper sx={{ overflow: 'hidden', mt: 2 }}>
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid',
          borderColor: tokens.line,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography variant="h3" component="h2" id={headingId} sx={{ flex: 1 }}>
          {t('statements.tenant.linesTitle', { period })}
        </Typography>
        <Button
          onClick={() => downloadCsv(statementFilename(slug, statement), statementCsv(statement))}
        >
          {t('statements.exportCsv')}
        </Button>
      </Box>
      {lines.length === 0 ? (
        <Typography variant="body2" sx={{ px: 2.5, py: 2, color: tokens.text2 }}>
          {t('statements.tenant.linesEmpty')}
        </Typography>
      ) : (
        <Table size="small" aria-labelledby={headingId}>
          <TableHead>
            <TableRow>
              <TableCell>{t('statements.tenant.col.kind')}</TableCell>
              <TableCell>{t('statements.tenant.col.description')}</TableCell>
              <TableCell align="right">{t('statements.tenant.col.quantity')}</TableCell>
              <TableCell align="right">{t('statements.tenant.col.amount')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map((line, index) => (
              <TableRow key={`${line.kind ?? 'line'}-${line.reference ?? index}`}>
                <TableCell>
                  {line.kind ? enumLabel(t, 'statements.tenant.kind', line.kind) : dash}
                </TableCell>
                <TableCell component="th" scope="row">
                  {line.description ?? dash}
                  {line.kind === 'AI' && typeof line.aiAtOurPriceMicro === 'number' ? (
                    <Typography
                      variant="caption"
                      component="div"
                      sx={{ color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}
                    >
                      {t('statements.tenant.aiAtPlatformPrice', {
                        amount: moneyMicroIn(line.aiAtOurPriceMicro, currency, locale, dash),
                      })}
                    </Typography>
                  ) : null}
                </TableCell>
                <NumericCell value={line.quantity} />
                <MoneyCell minor={line.amountMinor} currency={currency} />
              </TableRow>
            ))}
          </TableBody>
          <TotalsFooter>
            <TableCell component="th" scope="row" colSpan={3}>
              {t('statements.ledger.total')}
            </TableCell>
            <MoneyCell minor={statement.totalMinor} currency={currency} />
          </TotalsFooter>
        </Table>
      )}
    </Paper>
  );
}
